import { syncSidePanelPartsForWidth } from './voordeurConfigHelpers';

/** Minimum width (mm) for each Kozijnen frame part segment. */
export const MIN_PART_WIDTH_MM = 200;

/** Minimum width (mm) for door side panels (Terrasdeur, Achterdeur, Voordeur). */
export const MIN_SIDE_PANEL_WIDTH_MM = 200;

/** Minimum width (mm) for the door leaf section between side panels. */
export const MIN_DOOR_SECTION_WIDTH_MM = 200;

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

/**
 * Ensure part width array matches part count and sums exactly to totalWidth.
 */
export function ensurePartWidthsSum(partWidths, parts, totalWidth) {
  const total = Math.round(Number(totalWidth) || 0);
  const n = Math.max(1, Math.round(parts) || 1);
  if (n === 1) return [total];

  let widths =
    Array.isArray(partWidths) && partWidths.length === n
      ? partWidths.map((w) => Math.round(Number(w) || 0))
      : Array(n).fill(Math.floor(total / n));

  const sum = widths.reduce((acc, w) => acc + w, 0);
  if (sum === total) return widths;

  if (sum <= 0) {
    const equal = Math.floor(total / n);
    widths = Array(n).fill(equal);
    widths[n - 1] = total - equal * (n - 1);
    return widths;
  }

  const scaled = widths.map((w) => Math.round((w / sum) * total));
  const scaledSum = scaled.reduce((acc, w) => acc + w, 0);
  scaled[n - 1] += total - scaledSum;
  return scaled;
}

/**
 * When the user edits one part width, keep the total fixed:
 * 1) clamp the edited part (min 200 mm per part)
 * 2) the adjacent part absorbs the difference first
 * 3) any overflow is spread across the remaining flexible parts
 */
export function applyPartWidthChange(
  currentWidths,
  partIndex,
  newWidthMm,
  totalWidth,
  minPerPart = MIN_PART_WIDTH_MM
) {
  const total = Math.round(Number(totalWidth) || 0);
  const n = currentWidths?.length ?? 0;
  if (n <= 0) return [];
  if (n === 1) return [total];

  const widths = ensurePartWidthsSum(currentWidths, n, total);
  const index = clamp(Math.round(partIndex), 0, n - 1);
  const min = Math.max(1, Math.round(minPerPart));
  const maxForEdited = total - min * (n - 1);
  const clampedNew = clamp(Math.round(Number(newWidthMm) || 0), min, maxForEdited);
  const delta = clampedNew - widths[index];
  widths[index] = clampedNew;

  if (delta === 0) return widths;

  const neighborIndex = index < n - 1 ? index + 1 : index - 1;
  if (neighborIndex >= 0) {
    widths[neighborIndex] -= delta;
  }

  rebalanceToTotal(widths, total, min, new Set([index]));
  return widths;
}

function rebalanceToTotal(widths, totalWidth, minPerPart, pinnedIndices) {
  const n = widths.length;
  const maxPasses = n * 8;

  for (let pass = 0; pass < maxPasses; pass++) {
    let changed = false;

    for (let i = 0; i < n; i++) {
      if (widths[i] >= minPerPart) continue;
      const deficit = minPerPart - widths[i];
      widths[i] = minPerPart;
      if (!takeFromFlexibleParts(widths, deficit, minPerPart, pinnedIndices)) {
        changed = true;
      } else {
        changed = true;
      }
    }

    const sum = widths.reduce((acc, w) => acc + w, 0);
    const diff = totalWidth - sum;
    if (diff !== 0) {
      const flexIndices = widths
        .map((_, i) => i)
        .filter((i) => !pinnedIndices.has(i))
        .sort((a, b) => widths[b] - widths[a]);
      if (flexIndices.length > 0) {
        widths[flexIndices[0]] += diff;
        changed = true;
      }
    }

    const finalSum = widths.reduce((acc, w) => acc + w, 0);
    if (finalSum === totalWidth && widths.every((w) => w >= minPerPart)) {
      return;
    }

    if (!changed) break;
  }

  const flexIndices = widths.map((_, i) => i).filter((i) => !pinnedIndices.has(i));
  if (flexIndices.length > 0) {
    const last = flexIndices[flexIndices.length - 1];
    const sum = widths.reduce((acc, w) => acc + w, 0);
    widths[last] += totalWidth - sum;
  }
}

function takeFromFlexibleParts(widths, amount, minPerPart, pinnedIndices) {
  let remaining = amount;
  const donors = widths
    .map((w, i) => ({ i, w }))
    .filter(({ i, w }) => !pinnedIndices.has(i) && w > minPerPart)
    .sort((a, b) => b.w - a.w);

  for (const { i } of donors) {
    const take = Math.min(remaining, widths[i] - minPerPart);
    widths[i] -= take;
    remaining -= take;
    if (remaining <= 0) return true;
  }
  return remaining <= 0;
}

const hasLeftSidePanel = (designType) =>
  designType === 'left-panel' || designType === 'both-panels';

const hasRightSidePanel = (designType) =>
  designType === 'right-panel' || designType === 'both-panels';

const defaultSidePanelWidth = (totalWidth, hasLeft, hasRight) => {
  if (hasLeft && hasRight) return Math.round(totalWidth * 0.25);
  if (hasLeft || hasRight) return Math.round(totalWidth * 0.3);
  return 0;
};

export function resolveDoorLayoutWidths(
  designType,
  totalWidth,
  leftPanelWidth,
  rightPanelWidth
) {
  const total = Math.round(Number(totalWidth) || 0);
  const hasLeft = hasLeftSidePanel(designType);
  const hasRight = hasRightSidePanel(designType);
  const left = hasLeft
    ? Math.round(
        leftPanelWidth !== undefined && leftPanelWidth !== null
          ? Number(leftPanelWidth) || 0
          : defaultSidePanelWidth(total, hasLeft, hasRight)
      )
    : 0;
  const right = hasRight
    ? Math.round(
        rightPanelWidth !== undefined && rightPanelWidth !== null
          ? Number(rightPanelWidth) || 0
          : defaultSidePanelWidth(total, hasLeft, hasRight)
      )
    : 0;
  const door = total - (hasLeft ? left : 0) - (hasRight ? right : 0);
  return { hasLeft, hasRight, left, door, right };
}

/**
 * Edit one door-layout segment (left panel, door leaf, or right panel).
 * Total width stays fixed; other segments absorb the difference (min 200 mm each).
 */
export function applyDoorSegmentWidthChange({
  designType,
  totalWidth,
  leftPanelWidth,
  rightPanelWidth,
  changedSegment,
  newValueMm,
  minSegmentWidth = MIN_PART_WIDTH_MM,
}) {
  const total = Math.round(Number(totalWidth) || 0);
  const { hasLeft, hasRight, left, door, right } = resolveDoorLayoutWidths(
    designType,
    total,
    leftPanelWidth,
    rightPanelWidth
  );

  if (!hasLeft && !hasRight) return {};

  if (hasLeft && hasRight) {
    const widths = [left, door, right];
    const index =
      changedSegment === 'left' ? 0 : changedSegment === 'door' ? 1 : 2;
    const next = applyPartWidthChange(
      widths,
      index,
      newValueMm,
      total,
      minSegmentWidth
    );
    return { leftPanelWidth: next[0], rightPanelWidth: next[2] };
  }

  if (hasRight && !hasLeft) {
    const widths = [door, right];
    const index = changedSegment === 'door' ? 0 : 1;
    const next = applyPartWidthChange(
      widths,
      index,
      newValueMm,
      total,
      minSegmentWidth
    );
    return { rightPanelWidth: next[1] };
  }

  if (hasLeft && !hasRight) {
    const widths = [left, door];
    const index = changedSegment === 'left' ? 0 : 1;
    const next = applyPartWidthChange(
      widths,
      index,
      newValueMm,
      total,
      minSegmentWidth
    );
    return { leftPanelWidth: next[0] };
  }

  return {};
}

/**
 * Apply sub-ruler width edits from door configurators (never changes total width).
 * Returns an updated config partial, or null when the field is not a layout field.
 */
export function applyDoorLayoutFieldChange(prevConfig, field, value, options = {}) {
  const minDoorWidth = options.minDoorWidth ?? MIN_DOOR_SECTION_WIDTH_MM;
  const minPanelWidth = options.minPanelWidth ?? MIN_SIDE_PANEL_WIDTH_MM;
  const designType = prevConfig.designType || 'no-panel';
  const hasPanels = hasLeftSidePanel(designType) || hasRightSidePanel(designType);
  const layoutArgs = {
    designType,
    totalWidth: prevConfig.width,
    leftPanelWidth: prevConfig.leftPanelWidth,
    rightPanelWidth: prevConfig.rightPanelWidth,
    minDoorWidth,
    minPanelWidth,
  };

  if (field === 'doorSectionWidth' && hasPanels) {
    return applyDoorSegmentWidthChange({
      ...layoutArgs,
      changedSegment: 'door',
      newValueMm: value,
      minSegmentWidth: minDoorWidth,
    });
  }

  if (field === 'leftPanelWidth' && hasLeftSidePanel(designType)) {
    const layout = applyDoorSegmentWidthChange({
      ...layoutArgs,
      changedSegment: 'left',
      newValueMm: value,
      minSegmentWidth: minPanelWidth,
    });
    return {
      ...layout,
      ...syncSidePanelPartsForWidth(
        { ...prevConfig, ...layout },
        'left',
        layout.leftPanelWidth
      ),
    };
  }

  if (field === 'rightPanelWidth' && hasRightSidePanel(designType)) {
    const layout = applyDoorSegmentWidthChange({
      ...layoutArgs,
      changedSegment: 'right',
      newValueMm: value,
      minSegmentWidth: minPanelWidth,
    });
    return {
      ...layout,
      ...syncSidePanelPartsForWidth(
        { ...prevConfig, ...layout },
        'right',
        layout.rightPanelWidth
      ),
    };
  }

  if (field === 'width' && hasPanels) {
    const nextWidth = Math.round(Number(value) || prevConfig.width);
    const scaled = scaleSidePanelsForTotalWidth({
      designType,
      prevTotalWidth: prevConfig.width,
      nextTotalWidth: nextWidth,
      leftPanelWidth: prevConfig.leftPanelWidth,
      rightPanelWidth: prevConfig.rightPanelWidth,
      minPanelWidth,
      minDoorWidth,
    });
    const next = { width: nextWidth, ...scaled };
    return {
      ...next,
      ...(hasLeftSidePanel(designType)
        ? syncSidePanelPartsForWidth(
            { ...prevConfig, ...next },
            'left',
            next.leftPanelWidth
          )
        : {}),
      ...(hasRightSidePanel(designType)
        ? syncSidePanelPartsForWidth(
            { ...prevConfig, ...next },
            'right',
            next.rightPanelWidth
          )
        : {}),
    };
  }

  return null;
}

/**
 * Sync left/right side panel widths so left + door + right === totalWidth.
 * The door leaf absorbs remaining space; edited panel keeps its clamped value.
 */
export function applySidePanelWidthChange({
  designType,
  totalWidth,
  leftPanelWidth,
  rightPanelWidth,
  changedSide,
  newValueMm,
  minPanelWidth = MIN_SIDE_PANEL_WIDTH_MM,
  minDoorWidth = MIN_DOOR_SECTION_WIDTH_MM,
}) {
  void minDoorWidth;
  return applyDoorSegmentWidthChange({
    designType,
    totalWidth,
    leftPanelWidth,
    rightPanelWidth,
    changedSegment: changedSide === 'left' ? 'left' : 'right',
    newValueMm,
    minSegmentWidth: minPanelWidth,
  });
}

/**
 * When total width changes, scale side panels proportionally while keeping door >= minDoor.
 */
export function scaleSidePanelsForTotalWidth({
  designType,
  prevTotalWidth,
  nextTotalWidth,
  leftPanelWidth,
  rightPanelWidth,
  minPanelWidth = MIN_SIDE_PANEL_WIDTH_MM,
  minDoorWidth = MIN_DOOR_SECTION_WIDTH_MM,
}) {
  const prevTotal = Math.round(Number(prevTotalWidth) || 0);
  const nextTotal = Math.round(Number(nextTotalWidth) || 0);
  const hasLeft = hasLeftSidePanel(designType);
  const hasRight = hasRightSidePanel(designType);

  if (!hasLeft && !hasRight) {
    return { leftPanelWidth: undefined, rightPanelWidth: undefined };
  }

  if (prevTotal <= 0) {
    return applySidePanelWidthChange({
      designType,
      totalWidth: nextTotal,
      leftPanelWidth,
      rightPanelWidth,
      changedSide: hasLeft ? 'left' : 'right',
      newValueMm: hasLeft ? leftPanelWidth : rightPanelWidth,
      minPanelWidth,
      minDoorWidth,
    });
  }

  const ratio = nextTotal / prevTotal;
  let left = hasLeft ? Math.round((Number(leftPanelWidth) || 0) * ratio) : 0;
  let right = hasRight ? Math.round((Number(rightPanelWidth) || 0) * ratio) : 0;

  const min = Math.max(1, Math.round(minPanelWidth));
  const sideBudget = nextTotal - min;

  if (hasLeft && hasRight) {
    const sideSum = left + right;
    if (sideSum > sideBudget && sideSum > 0) {
      left = Math.round((left / sideSum) * sideBudget);
      right = sideBudget - left;
    }
    left = clamp(left, min, sideBudget - min);
    right = clamp(right, min, sideBudget - left);
  } else if (hasLeft) {
    left = clamp(left, min, sideBudget);
  } else if (hasRight) {
    right = clamp(right, min, sideBudget);
  }

  return {
    leftPanelWidth: hasLeft ? left : undefined,
    rightPanelWidth: hasRight ? right : undefined,
  };
}
